import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Archive, ArchiveRestore, CalendarDays, Calculator, Download, FileDown, FileSpreadsheet, FileText, HeartPulse, Palmtree, Paperclip, Pencil, Plus, Printer, ReceiptText, ScrollText, Trash2, Users, Wallet, X } from 'lucide-react';
import { AccesRefuse, Button, DataTable, FormField, Modal, PageHeader, StatusBadge, Tabs, WilayaSelect } from '@/components/ui';
import { Input } from '@/components/ui/input';
import type { StatusKey } from '@/types';
import { getApiErrorMessage } from '@/lib/axios';
import { can } from '@/lib/permissions';
import { useAuthStore } from '@/store/authStore';
import { usePerms } from '@/hooks/usePerms';
import { PointagesTab } from './PointagesTab';
import { CongesTab } from './CongesTab';
import { AvancesTab, ContratsTab, FichesPaieTab, VisitesTab } from './SuiviRhTabs';
import { DocumentsEmployeModal, ModelesDocumentsModal } from './DocumentsRh';
import {
  type BulletinDTO,
  type CalculPaieInput,
  type EmployeDTO,
  type EmployeInput,
  type EmployeStatut,
  EMPLOYE_STATUTS,
  MODES_PAIEMENT_RH,
  TYPES_CONTRAT,
  calculerPaie,
  createContrat,
  createEmploye,
  deleteEmploye,
  getApercuPaie,
  listEmployes,
  openBordereauCnas,
  openBulletinPdf,
  openFicheEmployePdf,
  updateEmploye,
} from '@/services/rh';
import { uploadFile } from '@/services/uploads';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

const SITUATIONS = ['CELIBATAIRE', 'MARIE', 'DIVORCE', 'VEUF'];
const SITUATION_LABEL: Record<string, string> = { CELIBATAIRE: 'Célibataire', MARIE: 'Marié(e)', DIVORCE: 'Divorcé(e)', VEUF: 'Veuf(ve)' };
const MODE_LABEL: Record<string, string> = { ESPECES: 'Espèces', CHEQUE: 'Chèque', VIREMENT: 'Virement', CARTE: 'Carte', PRELEVEMENT: 'Prélèvement' };
const STATUT_UI: Record<EmployeStatut, { label: string; status: StatusKey }> = {
  ACTIF: { label: 'Actif', status: 'done' },
  SUSPENDU: { label: 'Suspendu', status: 'stuck' },
  CONGE: { label: 'En congé', status: 'working' },
  SORTI: { label: 'Sorti', status: 'pending' },
};
const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';

export function RhPage() {
  const user = useAuthStore((s) => s.user);
  // Masquage par SOUS-PERMISSION « rh.<onglet> » : une sous-clé absente hérite de « rh »
  // (défaut additif, miroir du serveur). L'ADMIN voit tout (can() renvoie true).
  const sub = (key: string) => can(user, key, 'consulter');
  const isRh = sub('rh');  // module complet — pour le sous-titre uniquement
  // Pointages : cloisonnement chef de chantier (« pointage ») OU sous-permission « rh.pointages ».
  const isPointage = sub('rh.pointages') || can(user, 'pointage', 'consulter');

  // Onglet → sous-permission requise pour le voir.
  const allTabs = [
    { key: 'employes', label: 'Employés', icon: <Users className="size-4" />, show: sub('rh.employes') },
    { key: 'contrats', label: 'Contrats', icon: <ScrollText className="size-4" />, show: sub('rh.contrats') },
    { key: 'pointages', label: 'Pointages', icon: <CalendarDays className="size-4" />, show: isPointage },
    { key: 'conges', label: 'Congés & absences', icon: <Palmtree className="size-4" />, show: sub('rh.conges') },
    { key: 'paie', label: 'Calcul de paie', icon: <Calculator className="size-4" />, show: sub('rh.paie') },
    { key: 'fiches', label: 'Fiches de paie', icon: <FileText className="size-4" />, show: sub('rh.fiches_paie') },
    { key: 'avances', label: 'Avances salaire', icon: <Wallet className="size-4" />, show: sub('rh.avances') },
    { key: 'visites', label: 'Visites médicales', icon: <HeartPulse className="size-4" />, show: sub('rh.visites_medicales') },
  ];
  const tabs = allTabs.filter((t) => t.show);
  const [tab, setTab] = useState('pointages');
  // L'onglet courant est forcé sur le premier autorisé s'il n'est pas accessible.
  const current = tabs.some((t) => t.key === tab) ? tab : (tabs[0]?.key ?? '');
  useEffect(() => { if (current !== tab) setTab(current); }, [current, tab]);

  if (tabs.length === 0) {
    return <AccesRefuse message="Accès refusé" detail="Vous n'avez pas la permission d'accéder au module RH & paie." />;
  }

  const subtitle = isRh
    ? 'Employés, contrats, pointages, paie, avances et visites médicales'
    : 'Saisie des pointages (accès cloisonné chef de chantier)';

  return (
    <>
      <PageHeader
        title="RH & paie"
        subtitle={subtitle}
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'RH & paie' }]}
      />
      <Tabs
        tabs={tabs.map(({ key, label, icon }) => ({ key, label, icon }))}
        value={current}
        onChange={setTab}
        className="mb-6"
      />
      {/* Chaque contenu est doublement gardé (onglet courant + sous-permission). */}
      {current === 'employes' && sub('rh.employes') && <EmployesTab />}
      {current === 'contrats' && sub('rh.contrats') && <ContratsTab />}
      {current === 'pointages' && isPointage && <PointagesTab />}
      {current === 'conges' && sub('rh.conges') && <CongesTab />}
      {current === 'paie' && sub('rh.paie') && <PaieTab />}
      {current === 'fiches' && sub('rh.fiches_paie') && <FichesPaieTab />}
      {current === 'avances' && sub('rh.avances') && <AvancesTab />}
      {current === 'visites' && sub('rh.visites_medicales') && <VisitesTab />}
    </>
  );
}

function EmployesTab() {
  const queryClient = useQueryClient();
  const perms = usePerms('rh');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeDTO | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [docsEmploye, setDocsEmploye] = useState<EmployeDTO | null>(null);
  const [modelesOpen, setModelesOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['employes', { showArchived }], queryFn: () => listEmployes({ archives: showArchived }) });
  const { register, handleSubmit, reset, control } = useForm<EmployeInput>({
    defaultValues: { type_contrat: 'CDI', statut: 'ACTIF', mode_paiement: 'VIREMENT', nombre_enfants: 0, wilaya_id: null },
  });

  // Pièce jointe du contrat (scan / photo) saisie à la création de l'employé.
  const [contratDoc, setContratDoc] = useState<{ url: string; filename: string } | null>(null);
  const [contratRef, setContratRef] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const resetContrat = () => { setContratDoc(null); setContratRef(''); };

  const handleContratFile = async (file: File) => {
    setUploading(true);
    try {
      const res = await uploadFile(file);
      setContratDoc(res);
      toast.success('Contrat joint.');
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['employes'] }); queryClient.invalidateQueries({ queryKey: ['contrats'] }); };
  const openCreate = () => { setEditing(null); resetContrat(); reset({ type_contrat: 'CDI', statut: 'ACTIF', mode_paiement: 'VIREMENT', nombre_enfants: 0, wilaya_id: null, matricule: '', nom: '', prenom: '' }); setOpen(true); };
  const openEdit = (e: EmployeDTO) => { setEditing(e); resetContrat(); reset(e); setOpen(true); };

  const saveMutation = useMutation({
    mutationFn: async (v: EmployeInput) => {
      const payload = { ...v, salaire_base: Number(v.salaire_base) || 0, nombre_enfants: Number(v.nombre_enfants) || 0, wilaya_id: v.wilaya_id || null };
      if (editing) return updateEmploye(editing.id, payload);
      const cree = await createEmploye({ ...payload, matricule: v.matricule?.trim() || null });
      // Crée le contrat lié si une référence ou un scan a été fourni.
      if (contratDoc || contratRef.trim()) {
        await createContrat({
          employe_id: cree.id,
          reference: contratRef.trim() || null,
          type_contrat: payload.type_contrat,
          poste: payload.poste ?? null,
          salaire_base: payload.salaire_base,
          date_debut: payload.date_embauche ?? null,
          date_fin: payload.date_fin_contrat ?? null,
          actif: true,
          document_url: contratDoc?.url ?? null,
          notes: null,
        });
      }
      return cree;
    },
    onSuccess: (e) => { invalidate(); toast.success(editing ? 'Employé modifié.' : `Employé ${e.matricule} créé.`); reset(); resetContrat(); setOpen(false); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => updateEmploye(id, { is_archived: archived }),
    onSuccess: (_d, v) => { invalidate(); toast.success(v.archived ? 'Employé archivé.' : 'Employé restauré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteEmploye(id),
    onSuccess: () => { invalidate(); toast.success('Employé supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const columns: ColumnDef<EmployeDTO, unknown>[] = [
    { accessorKey: 'matricule', header: 'Matricule', size: 110 },
    {
      id: 'nom',
      header: 'Nom',
      accessorFn: (e) => `${e.prenom} ${e.nom}`,
      cell: ({ row }) => <span className="font-medium">{row.original.prenom} {row.original.nom}</span>,
    },
    { accessorKey: 'poste', header: 'Poste', cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'departement', header: 'Département', cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'type_contrat', header: 'Contrat', size: 90 },
    {
      accessorKey: 'salaire_base',
      header: 'Salaire base',
      cell: ({ getValue }) => <span className="tabular-nums">{formatDZD(getValue() as number)}</span>,
    },
    {
      accessorKey: 'statut',
      header: 'Statut',
      size: 100,
      cell: ({ getValue }) => {
        const s = STATUT_UI[getValue() as EmployeStatut] ?? STATUT_UI.ACTIF;
        return <StatusBadge status={s.status} label={s.label} />;
      },
    },
    {
      id: 'actions',
      header: '',
      size: 250,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDocsEmploye(row.original)} aria-label="Attestation / certificat de travail" title="Documents (attestation / certificat)"><ScrollText className="size-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openFicheEmployePdf(row.original.id, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Imprimer la fiche"><Printer className="size-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openFicheEmployePdf(row.original.id, 'download').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Télécharger la fiche"><Download className="size-4" /></Button>
          {showArchived ? (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: false })} aria-label="Restaurer"><ArchiveRestore className="size-4" /></Button>
          ) : (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row.original)} aria-label="Modifier"><Pencil className="size-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: true })} aria-label="Archiver"><Archive className="size-4" /></Button>
            </>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm(`Supprimer ${row.original.prenom} ${row.original.nom} ?`)) deleteMutation.mutate(row.original.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
          <input type="checkbox" className="size-4 accent-brand-primary" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Afficher les archivés
        </label>
        <Button variant="secondary" onClick={() => setModelesOpen(true)}><ScrollText /> Modèles de documents</Button>
        {perms.canAjouter && <Button onClick={openCreate}><Plus /> Nouvel employé</Button>}
      </div>
      <DataTable columns={columns} data={data?.items ?? []} loading={isLoading} pageSize={15} exportFileName="employes" emptyMessage={showArchived ? 'Aucun employé archivé.' : 'Aucun employé.'} />

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={editing ? "Modifier l'employé" : 'Nouvel employé'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={handleSubmit((v) => saveMutation.mutate(v))} loading={saveMutation.isPending}>{editing ? 'Enregistrer' : "Créer l'employé"}</Button>
          </>
        }
      >
        <div className="space-y-5">
          {/* État civil */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-secondary">État civil</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Matricule"><Input placeholder="Auto si vide (EMP-0001)" disabled={!!editing} {...register('matricule')} /></FormField>
              <FormField label="Statut">
                <select {...register('statut')} className={selectCls}>
                  {EMPLOYE_STATUTS.map((s) => <option key={s} value={s}>{STATUT_UI[s].label}</option>)}
                </select>
              </FormField>
              <FormField label="Prénom" required><Input {...register('prenom')} /></FormField>
              <FormField label="Nom" required><Input {...register('nom')} /></FormField>
              <FormField label="Date de naissance"><Input type="date" {...register('date_naissance')} /></FormField>
              <FormField label="Lieu de naissance"><Input {...register('lieu_naissance')} /></FormField>
              <FormField label="N° CNI (NIN)"><Input {...register('nin')} /></FormField>
              <FormField label="N° CNAS"><Input {...register('cnas_num')} /></FormField>
              <FormField label="Situation familiale">
                <select {...register('situation_familiale')} className={selectCls}>
                  <option value="">—</option>
                  {SITUATIONS.map((s) => <option key={s} value={s}>{SITUATION_LABEL[s]}</option>)}
                </select>
              </FormField>
              <FormField label="Nombre d'enfants"><Input type="number" step="1" min="0" {...register('nombre_enfants')} /></FormField>
            </div>
          </section>

          {/* Poste & contrat */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-secondary">Poste & contrat</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Poste"><Input {...register('poste')} /></FormField>
              <FormField label="Département"><Input {...register('departement')} /></FormField>
              <FormField label="Type de contrat">
                <select {...register('type_contrat')} className={selectCls}>
                  {TYPES_CONTRAT.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </FormField>
              <FormField label="Salaire de base (DZD)"><Input type="number" step="1" {...register('salaire_base')} /></FormField>
              <FormField label="Mode de paiement">
                <select {...register('mode_paiement')} className={selectCls}>
                  {MODES_PAIEMENT_RH.map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
                </select>
              </FormField>
              <FormField label="Date d'embauche"><Input type="date" {...register('date_embauche')} /></FormField>
              <FormField label="Fin de contrat (CDD)"><Input type="date" {...register('date_fin_contrat')} /></FormField>
            </div>
            {!editing && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField label="Référence du contrat">
                  <Input placeholder="Ex. CDI-2026-001" value={contratRef} onChange={(e) => setContratRef(e.target.value)} />
                </FormField>
                <FormField label="Contrat scanné (PDF / photo)">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,application/pdf"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleContratFile(f); }}
                  />
                  {contratDoc ? (
                    <div className="flex h-10 items-center justify-between rounded-md border border-status-done/40 bg-status-done/10 px-3 text-sm">
                      <span className="flex items-center gap-2 truncate text-content-primary"><Paperclip className="size-4 shrink-0" /> <span className="truncate">{contratDoc.filename}</span></span>
                      <button type="button" onClick={resetContrat} className="rounded p-1 text-content-muted hover:text-status-stuck" aria-label="Retirer"><X className="size-4" /></button>
                    </div>
                  ) : (
                    <Button type="button" variant="secondary" className="w-full" onClick={() => fileRef.current?.click()} loading={uploading}>
                      <Paperclip /> Prendre en photo / joindre le contrat
                    </Button>
                  )}
                </FormField>
              </div>
            )}
          </section>

          {/* Coordonnées */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-secondary">Coordonnées</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Téléphone"><Input {...register('telephone')} /></FormField>
              <FormField label="Email"><Input type="email" {...register('email')} /></FormField>
              <FormField label="Adresse" className="sm:col-span-2"><Input {...register('adresse')} /></FormField>
              <FormField label="Ville"><Input {...register('ville')} /></FormField>
              <FormField label="Wilaya">
                <Controller control={control} name="wilaya_id" render={({ field }) => <WilayaSelect value={field.value ?? null} onChange={field.onChange} />} />
              </FormField>
            </div>
          </section>

          {/* RIB */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-secondary">RIB (paiement du salaire)</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <FormField label="Banque"><Input maxLength={3} placeholder="000" {...register('rib_banque')} /></FormField>
              <FormField label="Agence"><Input maxLength={5} placeholder="00000" {...register('rib_agence')} /></FormField>
              <FormField label="Compte"><Input maxLength={12} {...register('rib_compte')} /></FormField>
              <FormField label="Clé"><Input maxLength={2} placeholder="00" {...register('rib_cle')} /></FormField>
            </div>
          </section>
        </div>
      </Modal>

      <DocumentsEmployeModal employe={docsEmploye} open={!!docsEmploye} onOpenChange={(v) => { if (!v) setDocsEmploye(null); }} />
      <ModelesDocumentsModal open={modelesOpen} onOpenChange={setModelesOpen} />
    </div>
  );
}

function PaieTab() {
  const now = new Date();
  const { data: employes } = useQuery({ queryKey: ['employes'], queryFn: () => listEmployes({ actifs_seulement: true }) });
  const [bulletin, setBulletin] = useState<BulletinDTO | null>(null);
  const [showAvances, setShowAvances] = useState(false);
  const { register, handleSubmit, watch, setValue } = useForm<CalculPaieInput>({
    defaultValues: { mois: now.getMonth() + 1, annee: now.getFullYear(), jours_ouvrables: 26 },
  });

  // Aperçu : dès qu'un employé + un mois sont choisis, on pré-remplit le salaire
  // (contrat), les jours travaillés (pointage), la prime d'ancienneté, et on liste
  // les avances du mois. Les champs restent éditables.
  const employeId = watch('employe_id');
  const moisW = Number(watch('mois')) || 0;
  const anneeW = Number(watch('annee')) || 0;
  const { data: apercu } = useQuery({
    queryKey: ['paie-apercu', employeId, moisW, anneeW],
    queryFn: () => getApercuPaie(employeId, moisW, anneeW),
    enabled: !!employeId && moisW >= 1 && moisW <= 12 && anneeW >= 2000,
  });
  useEffect(() => {
    if (!apercu) return;
    setValue('salaire_base', apercu.salaire_base);
    setValue('jours_ouvrables', apercu.jours_ouvrables);
    setValue('jours_travailles', apercu.jours_travailles);
    setValue('iep_montant', apercu.iep_montant);
  }, [apercu, setValue]);
  const numOrUndef = (x: unknown) => (x === '' || x === undefined || x === null ? undefined : Number(x));

  const handleBordereau = async (mode: 'open' | 'download') => {
    try {
      await openBordereauCnas(Number(watch('mois')), Number(watch('annee')), mode);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };
  const handleBulletinPdf = async (mode: 'open' | 'download') => {
    if (!bulletin) return;
    try {
      await openBulletinPdf(bulletin.id, mode);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const mutation = useMutation({
    mutationFn: (v: CalculPaieInput) =>
      calculerPaie({
        ...v,
        mois: Number(v.mois),
        annee: Number(v.annee),
        salaire_base: numOrUndef(v.salaire_base),
        jours_ouvrables: Number(v.jours_ouvrables) || 26,
        jours_travailles: numOrUndef(v.jours_travailles),
        iep_montant: numOrUndef(v.iep_montant),
        heures_sup_75: Number(v.heures_sup_75) || 0,
        heures_sup_100: Number(v.heures_sup_100) || 0,
        prime_rendement: Number(v.prime_rendement) || 0,
        prime_chantier: Number(v.prime_chantier) || 0,
        indemnite_transport: Number(v.indemnite_transport) || 0,
        indemnite_panier: Number(v.indemnite_panier) || 0,
        indemnite_zone: Number(v.indemnite_zone) || 0,
        indemnite_nuisance: Number(v.indemnite_nuisance) || 0,
        allocations_familiales: Number(v.allocations_familiales) || 0,
        conjoint_a_charge: !!v.conjoint_a_charge,
        handicape_retraite: !!v.handicape_retraite,
      }),
    onSuccess: (b) => {
      setBulletin(b);
      toast.success('Bulletin calculé.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  type Ligne = { label: string; val: number; kind: 'gain' | 'retenue' | 'total'; opt?: boolean; text?: string };
  const lignes: Ligne[] = (bulletin
    ? ([
        { label: 'Salaire de base', val: bulletin.salaire_base, kind: 'gain' },
        { label: 'Retenue pour absence', val: -bulletin.retenue_absence, kind: 'retenue', opt: true },
        { label: "IEP (prime d'ancienneté)", val: bulletin.iep_montant, kind: 'gain', opt: true },
        { label: `Heures supplémentaires${bulletin.heures_sup_total ? ` (${bulletin.heures_sup_total} h)` : ''}`, val: bulletin.heures_sup_montant, kind: 'gain', opt: true },
        { label: 'Prime de rendement', val: bulletin.prime_rendement, kind: 'gain', opt: true },
        { label: 'Prime de chantier', val: bulletin.prime_chantier, kind: 'gain', opt: true },
        { label: 'Autres primes', val: bulletin.autres_primes, kind: 'gain', opt: true },
        { label: 'Indemnité de transport', val: bulletin.indemnite_transport, kind: 'gain', opt: true },
        { label: 'Indemnité de panier', val: bulletin.indemnite_panier, kind: 'gain', opt: true },
        { label: 'Indemnité de zone', val: bulletin.indemnite_zone, kind: 'gain', opt: true },
        { label: 'Indemnité de nuisance', val: bulletin.indemnite_nuisance, kind: 'gain', opt: true },
        { label: 'Allocations familiales', val: bulletin.allocations_familiales, kind: 'gain', opt: true },
        { label: 'CNAS salarié (9 %)', val: -bulletin.cnas_salarie_montant, kind: 'retenue' },
        { label: 'IRG', val: -bulletin.irg_montant, kind: 'retenue', text: bulletin.irg_montant === 0 ? 'néant' : undefined },
        { label: 'Autres retenues', val: -bulletin.autres_retenues, kind: 'retenue', opt: true },
        { label: 'Avance sur salaire', val: -bulletin.avances, kind: 'retenue', opt: true },
        { label: 'Net à payer', val: bulletin.net_a_payer, kind: 'total' },
      ] as Ligne[])
    : []
  ).filter((l) => !l.opt || l.val !== 0);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-4 text-base font-bold text-content-primary">Paramètres de calcul</h2>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Employé" required className="col-span-2">
            <select {...register('employe_id')} className={selectCls}>
              <option value="">— Sélectionner —</option>
              {employes?.items.map((e) => <option key={e.id} value={e.id}>{e.matricule} · {e.prenom} {e.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Mois"><Input type="number" step="1" min="1" max="12" {...register('mois')} /></FormField>
          <FormField label="Année"><Input type="number" step="1" {...register('annee')} /></FormField>
          {/* Axe 1 — Salaire de base : pré-rempli du contrat, toujours éditable. */}
          <FormField label="Salaire de base (DA)" required className="col-span-2">
            <Input type="number" step="1" placeholder="depuis le contrat / la fiche" {...register('salaire_base')} />
            {apercu && (
              <p className="mt-1 text-xs text-content-muted">
                Source : {apercu.salaire_source === 'contrat' ? 'contrat actif' : apercu.salaire_source === 'fiche' ? 'fiche employé' : 'à saisir (aucun salaire enregistré)'}
              </p>
            )}
          </FormField>
          {/* Axe 2 — Jours travaillés (pointage) + jours ouvrables = diviseur (26). */}
          <FormField label="Jours travaillés">
            <Input type="number" step="0.5" min="0" max="31" {...register('jours_travailles')} />
            {apercu && (
              <p className={`mt-1 text-xs ${apercu.pointage_source === 'cloture' ? 'text-content-muted' : 'text-status-stuck'}`}>
                {apercu.pointage_source === 'cloture'
                  ? 'Source : pointage clôturé'
                  : apercu.pointage_source === 'non_cloture'
                    ? 'Pointage non clôturé — vérifiez la valeur'
                    : 'Pointage du mois absent — saisie manuelle'}
              </p>
            )}
          </FormField>
          <FormField label="Jours ouvrables (diviseur)"><Input type="number" step="1" min="1" max="31" {...register('jours_ouvrables')} /></FormField>
          {/* Axe 4 — Prime d'ancienneté (IEP) : calcul auto depuis la date d'embauche, éditable. */}
          <FormField label="Prime ancienneté (DA)" className="col-span-2">
            <Input type="number" step="1" {...register('iep_montant')} />
            {apercu && (
              <p className={`mt-1 text-xs ${apercu.date_embauche_presente ? 'text-content-muted' : 'text-status-stuck'}`}>
                {apercu.date_embauche_presente
                  ? `${apercu.anciennete_annees} an(s) d'ancienneté → ${Number(apercu.iep_taux_pct)} %`
                  : "Date d'embauche manquante dans le contrat — ancienneté à 0"}
              </p>
            )}
          </FormField>
          {/* Axe 5 — heures sup. spéciales (les 25 %/50 % viennent du pointage). */}
          <FormField label="H. sup. fériés (75 %)"><Input type="number" step="1" {...register('heures_sup_75')} /></FormField>
          <FormField label="H. sup. férié nuit (100 %)"><Input type="number" step="1" {...register('heures_sup_100')} /></FormField>
          <FormField label="Prime rendement"><Input type="number" step="1" {...register('prime_rendement')} /></FormField>
          <FormField label="Prime chantier"><Input type="number" step="1" {...register('prime_chantier')} /></FormField>
          <FormField label="Ind. transport"><Input type="number" step="1" {...register('indemnite_transport')} /></FormField>
          <FormField label="Ind. panier"><Input type="number" step="1" {...register('indemnite_panier')} /></FormField>
          <FormField label="Ind. zone"><Input type="number" step="1" {...register('indemnite_zone')} /></FormField>
          <FormField label="Ind. nuisance"><Input type="number" step="1" {...register('indemnite_nuisance')} /></FormField>
          <FormField label="Alloc. familiales"><Input type="number" step="1" {...register('allocations_familiales')} /></FormField>
        </div>
        {/* Axe 3 — Avances du mois (lecture seule), déduites automatiquement du net. */}
        <div className="mt-4 rounded-md border border-border bg-surface-primary/40 px-3 py-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-content-secondary">Avances du mois (DA)</span>
            <div className="flex items-center gap-2">
              <span className="font-bold tabular-nums text-content-primary">{formatDZD(apercu?.avances_total ?? 0)}</span>
              {!!apercu?.avances.length && (
                <button type="button" onClick={() => setShowAvances((s) => !s)} className="text-xs font-semibold text-brand-primary hover:underline">
                  {showAvances ? 'masquer' : 'détail'}
                </button>
              )}
            </div>
          </div>
          {showAvances && apercu?.avances.map((a) => (
            <div key={a.id} className="mt-1 flex justify-between text-xs text-content-muted">
              <span>{a.date_avance}{a.motif ? ` · ${a.motif}` : ''}</span>
              <span className="tabular-nums">{formatDZD(a.montant)}</span>
            </div>
          ))}
          <p className="mt-1 text-[11px] text-content-muted">Avances accordées du mois — déduites automatiquement du net à payer.</p>
        </div>
        <div className="mt-3 flex flex-wrap gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
            <input type="checkbox" className="size-4 accent-brand-primary" {...register('conjoint_a_charge')} />
            Conjoint à charge (sans revenus)
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
            <input type="checkbox" className="size-4 accent-brand-primary" {...register('handicape_retraite')} />
            Salarié handicapé / retraité
          </label>
        </div>
        <p className="mt-3 text-xs text-content-muted">Salaire de base, jours travaillés et prime d'ancienneté sont pré-remplis (contrat, pointage, date d'embauche) et restent modifiables. Les heures sup. courantes <strong>25 %</strong> (jour) et <strong>50 %</strong> (repos/nuit) proviennent du pointage ; saisissez ci-dessus les heures sur <strong>jours fériés (75 %)</strong> et <strong>fériés de nuit (100 %)</strong>. Heures sup. exonérées d'IRG (barème LF 2022). Allocations familiales auto selon le nombre d'enfants si laissées à 0.</p>
        <Button type="submit" className="mt-4 w-full" loading={mutation.isPending}>
          <Calculator /> Calculer le bulletin
        </Button>
        <div className="mt-2 flex gap-2">
          <Button type="button" variant="secondary" className="flex-1" onClick={() => handleBordereau('open')}>
            <FileSpreadsheet /> Bordereau CNAS
          </Button>
          <Button type="button" variant="secondary" onClick={() => handleBordereau('download')} aria-label="Télécharger le bordereau CNAS">
            <Download />
          </Button>
        </div>
      </form>

      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-content-primary"><ReceiptText className="size-4 text-brand-primary" /> Bulletin de paie</h2>
        {!bulletin ? (
          <p className="py-12 text-center text-sm text-content-muted">Renseignez les paramètres et calculez.</p>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between rounded-md bg-brand-primary/5 px-3 py-2 text-xs">
              <span className="text-content-secondary">Jours payés (issus du pointage)</span>
              <span className="font-semibold text-content-primary">
                {bulletin.jours_travailles} / {bulletin.jours_ouvrables} jours
                {bulletin.heures_sup_total ? ` · ${bulletin.heures_sup_total} h sup.` : ''}
              </span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {lignes.map(({ label, val, kind, text }) => (
                  <tr key={label} className={kind === 'total' ? 'border-t-2 border-border' : 'border-b border-border'}>
                    <td className={`py-2 ${kind === 'total' ? 'font-bold text-content-primary' : 'text-content-secondary'}`}>{label}</td>
                    <td className={`py-2 text-right tabular-nums ${kind === 'total' ? 'text-lg font-bold text-brand-primary' : kind === 'retenue' ? 'text-status-stuck' : 'text-content-primary'}`}>
                      {text ?? formatDZD(val)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 rounded-md bg-surface-primary p-3 text-xs text-content-secondary">
              <div className="flex justify-between"><span>CNAS patronal (26 %)</span><span className="tabular-nums">{formatDZD(bulletin.cnas_patronal_montant)}</span></div>
              <div className="mt-1 flex justify-between font-semibold"><span>Coût total employeur</span><span className="tabular-nums">{formatDZD(bulletin.cout_total_employeur)}</span></div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => handleBulletinPdf('open')}>
                <Printer /> Imprimer le bulletin
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => handleBulletinPdf('download')}>
                <FileDown /> Télécharger
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
